import { authQueryString, getAuthConfig } from "./auth-config";
import { azimutRefusal, problemType } from "../auth/azimutRefusal";
/**
 * Error thrown by {@link ApiService} on a non-2xx response.
 *
 * Carries the HTTP `status` and the raw response `body` as fields so callers
 * can branch on a code (404 → "no data", 402 → paywall) or surface the
 * backend's message without parsing `message` by hand. The message keeps the
 * historical `HTTP error {status}: {body}` shape, so handlers that match on
 * it keep working.
 */
export class ApiError extends Error {
    constructor(status, body) {
        super(`HTTP error ${status}: ${body}`);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
    /** The problem `type` of a problem+json body (its last segment), or `null`. */
    get problemType() {
        return problemType(this.body);
    }
}
export class ApiService {
    constructor(options) {
        this.baseUrl = options.baseUrl;
        this.token = options.token;
    }
    getHeaders() {
        const auth = getAuthConfig();
        return {
            "Content-Type": "application/json",
            ...(auth
                ? auth.headers()
                : { "ggo-short-token": localStorage.getItem('ggo_token') || this.token }),
        };
    }
    currentToken() {
        return localStorage.getItem('ggo_token') || this.token;
    }
    async request(endpoint, options = {}) {
        const send = () => fetch(`${this.baseUrl}${endpoint}`, {
            ...options,
            headers: {
                ...this.getHeaders(),
                ...options.headers,
            },
        });
        let res = await send();
        // One retry after a 401 when the configured auth can renew its credential.
        const onUnauthorized = getAuthConfig()?.onUnauthorized;
        if (res.status === 401 && onUnauthorized && (await onUnauthorized())) {
            res = await send();
        }
        if (!res.ok) {
            const text = await res.text();
            // A refusal of the session reaches the app's interface, not only the
            // caller's catch.
            const onRefused = getAuthConfig()?.onRefused;
            const refusal = onRefused ? azimutRefusal(res.status, text) : null;
            if (onRefused && refusal)
                onRefused(refusal);
            throw new ApiError(res.status, text);
        }
        // Si réponse vide
        if (res.status === 204)
            return {};
        return res.json();
    }
    get(endpoint) {
        return this.request(endpoint, { method: "GET" });
    }
    post(endpoint, body) {
        return this.request(endpoint, {
            method: "POST",
            body: JSON.stringify(body),
        });
    }
    put(endpoint, body) {
        return this.request(endpoint, {
            method: "PUT",
            body: JSON.stringify(body),
        });
    }
    delete(endpoint) {
        return this.request(endpoint, { method: "DELETE" });
    }
    webhook(key, data) {
        return this.post(`/webhook/${key}`, data).then((res) => res.data);
    }
    waitForNodeCompletion(workflow_execution_id, key, interval = 2000, max_tries = 5) {
        return new Promise((resolve, reject) => {
            let attempts = 0; // Counter for the number of tries
            const checkStatus = async () => {
                if (attempts >= max_tries && max_tries > 0) {
                    reject(new Error(`Max retries reached (${max_tries}) for node execution`));
                    return;
                }
                attempts++; // Increment the attempt counter
                try {
                    const res = await this.get(`/workflow_execution/${workflow_execution_id}/node_execution/${key}`);
                    if (res.status === 'completed') {
                        resolve(res);
                    }
                    else if (res.status === 'failed') {
                        reject(new Error(`Node execution failed: ${res.message || 'Unknown error'}`));
                    }
                    else {
                        setTimeout(checkStatus, interval);
                    }
                }
                catch (error) {
                    reject(error);
                }
            };
            checkStatus();
        });
    }
    async awake() {
        const res = await fetch(`${this.baseUrl}/health`, {
            method: 'GET',
            headers: this.getHeaders(),
        });
        if (!res.ok) {
            throw new Error(`API not healthy: ${res.status}`);
        }
    }
    websocket_wf_execution(executionId) {
        const wsUrl = `${this.baseUrl.replace(/^http/, 'ws')}/ws/execution/${executionId}?${authQueryString(this.currentToken())}`;
        const ws = new WebSocket(wsUrl);
        ws.onopen = () => console.log(`WebSocket connected for execution ${executionId}`);
        ws.onmessage = (event) => console.log(`WebSocket message for execution ${executionId}:`, event.data);
        ws.onerror = (err) => console.error(`WebSocket error for execution ${executionId}`, err);
        ws.onclose = () => console.log(`WebSocket disconnected for execution ${executionId}`);
        // Optionnel : exposer un stop() pour fermer manuellement
        ws.stop = () => {
            ws.close(1000, 'Client stopped listening');
        };
        return ws;
    }
}
//# sourceMappingURL=api.service.js.map